import React, { useState } from 'react';
import { Button } from '../../ui/button';
import Clause from './Clause';
import { Tooltip } from '../../ui/tooltip';
import { Info, X } from 'lucide-react';

const Segmented = ({ value, onChange, options, tiny = false }) => (
  <div className={`inline-flex items-center ${tiny ? 'p-0.5 rounded-md' : 'p-1 rounded-md'} bg-muted`}>
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        onClick={() => onChange(o.value)}
        className={`px-2 ${tiny ? 'py-0.5 text-xs' : 'py-1 text-sm'} rounded-md transition ${
          value === o.value ? 'bg-background text-textPrimary shadow-sm' : 'text-textSecondary hover:text-textPrimary'
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const MiniButton = ({ children, variant = 'ghost', ...rest }) => (
  <button
    {...rest}
    className={`h-7 px-2 text-xs rounded-md border transition ${
      variant === 'ghost'
        ? 'border-transparent hover:bg-muted/50 text-textSecondary'
        : 'border-borderDefault bg-background hover:bg-muted'
    }`}
  >
    {children}
  </button>
);

/**
 * Recursive RuleBuilder (compact)
 * spec: { type:'group', op:'AND'|'OR', clauses:(Group|Clause)[] }
 */
const RuleBuilder = ({ spec, onChange, onRemove, section, isNested = false, baseTimeframe = 'M1' }) => {
  const [collapsed, setCollapsed] = useState(false);
  const onChangeSafe = onChange || (() => {});
  const getBase = (prev) => {
    const type = prev?.type || 'group';
    const op = prev?.op || 'AND';
    const clauses = Array.isArray(prev?.clauses) ? prev.clauses : [];
    return { ...(prev || {}), type, op, clauses };
  };
  const currentOp = spec?.op || 'AND';
  const clauses = Array.isArray(spec?.clauses) ? spec.clauses : [];

  // local helpers
  // Allow object or functional updates so inner components can always work with freshest clause
  const summarizeClause = (c) => {
    if (!c) return 'null';
    if (c.type === 'group') return `group(op=${c.op}, clauses=${c.clauses?.length})`;
    const lhsLen = c?.lhs?.params?.length;
    const rhsLen = c?.rhs?.params?.length;
    return `clause(op=${c.op}, lhsLen=${lhsLen}, rhsLen=${rhsLen})`;
  };
  const replaceClause = (i, next) => {
    onChangeSafe((prevSpec) => {
      const base = getBase(prevSpec);
      const current = base.clauses[i];
      const updated = typeof next === 'function' ? next(current) : next;
  if (import.meta.env?.DEV) {
        console.log('[RuleBuilder] replaceClause', {
          section,
          index: i,
          before: summarizeClause(current),
          after: summarizeClause(updated),
          beforeObj: current,
          afterObj: updated,
        });
      }
      return { ...base, clauses: base.clauses.map((c, idx) => (idx === i ? updated : c)) };
    });
  };
  const removeClause = (i) => {
    onChangeSafe((prevSpec) => {
      const base = getBase(prevSpec);
  if (import.meta.env?.DEV) {
        console.log('[RuleBuilder] removeClause', { section, index: i, removed: summarizeClause(base.clauses[i]) });
      }
      return { ...base, clauses: base.clauses.filter((_, idx) => idx !== i) };
    });
  };
  const genId = () => Math.random().toString(36).slice(2, 10);
  const addClause = () => {
    const newClause = {
      id: genId(),
      type: 'clause',
      lhs: { type: 'indicator', name: 'ema', params: { length: 21 } },
      op: 'crosses_above',
      rhs: { type: 'indicator', name: 'ema', params: { length: 50 } },
    };
  if (import.meta.env?.DEV) {
      console.log('[RuleBuilder] addClause', { section, newClause: summarizeClause(newClause) });
    }
  onChangeSafe((prevSpec) => {
      const base = getBase(prevSpec);
      return { ...base, clauses: [...base.clauses, newClause] };
    });
  };
  const addGroup = () => {
    const grp = { id: genId(), type: 'group', op: 'AND', clauses: [] };
  if (import.meta.env?.DEV) {
      console.log('[RuleBuilder] addGroup', { section, group: grp });
    }
  onChangeSafe((prevSpec) => {
      const base = getBase(prevSpec);
      return { ...base, clauses: [...base.clauses, grp] };
    });
  };
  const duplicateClause = (i) => {
    onChangeSafe((prevSpec) => {
      const base = getBase(prevSpec);
      const src = base.clauses[i];
      if (!src) return base;
      const clone = JSON.parse(JSON.stringify(src));
      clone.id = genId();
      const newClauses = [
        ...base.clauses.slice(0, i + 1),
        clone,
        ...base.clauses.slice(i + 1),
      ];
      if (import.meta.env?.DEV) {
        const summarize = (c) => (c?.type === 'group' ? `group(op=${c.op}, clauses=${c.clauses?.length})` : `clause(op=${c?.op})`);
        console.log('[RuleBuilder] duplicateClause', {
          section,
          index: i,
          src: summarize(src),
          inserted: summarize(clone),
        });
      }
      return { ...base, clauses: newClauses };
    });
  };
  const handleOperatorChange = (op) => {
  if (import.meta.env?.DEV) {
      console.log('[RuleBuilder] groupOpChange', { section, from: spec?.op, to: op });
    }
  onChangeSafe((prevSpec) => {
      const base = getBase(prevSpec);
      return { ...base, op };
    });
  };

  return (
    <div className={`rounded-lg border border-borderDefault ${isNested ? 'ml-3' : ''} bg-card/60`}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-borderDefault bg-background/60">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className="size-6 grid place-items-center rounded hover:bg-muted/70 text-textSecondary"
            aria-label={collapsed ? 'Expand group' : 'Collapse group'}
          >
            {collapsed ? '▸' : '▾'}
          </button>
          <span className="text-xs font-medium">
            {section?.toUpperCase()} {isNested ? '• Group' : '• Root'}
          </span>
          <span className="text-xs text-muted-foreground">• {currentOp} • {clauses.length} item(s)</span>
          <Tooltip content="All clauses within this group combine with the selected operator.">
            <span className="inline-flex items-center text-textSecondary ml-1">
              <Info size={14} />
            </span>
          </Tooltip>
        </div>

        <div className="flex items-center gap-2">
          <Segmented
            tiny
            value={currentOp}
            onChange={handleOperatorChange}
            options={[{ label: 'AND', value: 'AND' }, { label: 'OR', value: 'OR' }]}
          />
          <div className="h-5 w-px bg-borderDefault" />
          <MiniButton onClick={addClause}>+ Clause</MiniButton>
          <MiniButton onClick={addGroup}>+ Group</MiniButton>
          {isNested && onRemove && (
            <>
              <div className="h-5 w-px bg-borderDefault" />
              <MiniButton onClick={onRemove} variant="destructive">
                <X size={14} />
              </MiniButton>
            </>
          )}
        </div>
      </div>

      {/* Body */}
      {!collapsed && (
        <div className="pl-3 pr-2 py-2 border-l border-borderDefault">
          {clauses.length === 0 && (
            <div className="px-2 py-3 text-sm text-textSecondary">No clauses yet. Add a clause or a group.</div>
          )}

          <ul className="space-y-2">
            {clauses.map((clause, idx) => (
              <React.Fragment key={clause?.id || idx}>
                {idx > 0 && (
                  <div className="my-2 flex items-center justify-center">
                    <div className="h-px flex-grow bg-borderDefault" />
                    <button
                      type="button"
                      onClick={() => handleOperatorChange((currentOp) === 'AND' ? 'OR' : 'AND')}
                      className={`mx-2 px-2.5 py-1 text-xs font-semibold rounded-full border-2 transition-colors ${
                        (currentOp) === 'OR'
                          ? 'bg-amber-100/70 border-amber-300/80 text-amber-800 hover:bg-amber-100'
                          : 'bg-gray-100/70 border-gray-300/80 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {currentOp}
                    </button>
                    <div className="h-px flex-grow bg-borderDefault" />
                  </div>
                )}

                {clause?.type === 'group' ? (
                  <RuleBuilder
                    spec={clause}
                    onChange={(updated) => replaceClause(idx, updated)}
                    onRemove={() => removeClause(idx)}
                    section={section}
                    isNested
                  />
                ) : (
          <li>
                    <Clause
                      clause={clause?.type === 'clause' ? clause : { type: 'clause', ...(clause || {}) }}
                      onUpdate={(updater) => replaceClause(idx, (prev) => {
                        const base = prev?.type === 'clause' ? prev : { type: 'clause', ...(prev || {}) };
                        const next = typeof updater === 'function' ? updater(base) : updater;
                        return { ...base, ...next };
                      })}
                      onDuplicate={() => duplicateClause(idx)}
                      onRemove={() => removeClause(idx)}
            baseTimeframe={baseTimeframe}
                    />
                  </li>
                )}
              </React.Fragment>
            ))}
          </ul>

          {/* footer (kept for mobile reachability) */}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button type="button" className="h-8" onClick={addClause}>
              Add Clause
            </Button>
            <Button type="button" className="h-8" onClick={addGroup} variant="secondary">
              Add Group
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RuleBuilder;
